/**
 * @file Users settings invite rendering and actions.
 */

import { appData } from "../../app/state/state.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    formatDateTime,
} from "../../shared/utils/dateUtils.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { deleteUserInvite, sendUserInvite } from "./userLifecycleApi.js";
import { openUserInviteForm } from "./userFormModal.js";

let showClaimedInvites = false;

function getInviteConflictModalConfig(message) {
  const normalizedMessage = String(message || "").toLowerCase();
  if (normalizedMessage.includes("invite for this email already exists")) {
    return {
      title: "Error: Invite already exists",
      body: "An invite for this email already exists. You can resend the invite by clicking on the email button.",
    };
  }
  if (normalizedMessage.includes("inactive user with the same email exists")) {
    return {
      title: "Error: User already exists",
      body: "An inactive user with the same email exists. If you are inviting the same person, reactivate their account instead. If this is a different person, use a different email.",
    };
  }
  if (normalizedMessage.includes("active user with the same email exists")) {
    return {
      title: "Error: User already exists",
      body: "An active user with the same email exists.",
    };
  }
  return null;
}

function getInviteStatusLabel(status) {
  switch (status) {
    case "sent":
      return "Sent";
    case "claimed":
      return "Claimed";
    default:
      return "Draft";
  }
}

function getInviteStatusClasses(status) {
  switch (status) {
    case "sent":
      return "bg-blue-50 text-blue-700 border border-blue-200";
    case "claimed":
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    default:
      return "bg-amber-50 text-amber-700 border border-amber-200";
  }
}

function formatInviteTimestamp(value) {
  if (!value) {
    return "Never";
  }
  return formatDateTime(value);
}

function getInviteSenderLabel(invite) {
  const senderId = invite?.lastSentBy || null;
  if (!senderId) {
    return "Not sent";
  }

  const sender = (appData.users || []).find((user) => user.id === senderId);
  if (!sender) {
    return "Unknown user";
  }

  return sender.name || sender.email || "Unknown user";
}

function getInviteManagerLabel(invite) {
  const managerUserId = invite?.managerUserId || "";
  if (!managerUserId) {
    return "Manager not set";
  }
  if (managerUserId === "none") {
    return "No manager";
  }

  const manager = (appData.users || []).find((user) => user.id === managerUserId);
  if (!manager) {
    return "Unknown manager";
  }

  const inactiveSuffix = manager.isActive === false ? " (inactive)" : "";
  return `${manager.name || manager.email || "Unknown manager"}${inactiveSuffix}`;
}

function renderInviteAccessSummary(invite) {
  if (invite.role === "admin") {
    return [
      '<span class="text-xs text-gray-600">Admin access</span>',
      `<span class="mt-1 block text-xs text-gray-500">Manager: ${sanitizeHTML(getInviteManagerLabel(invite))}</span>`,
    ].join("");
  }

  const permissionLabels =
    Array.isArray(invite.permissions) && invite.permissions.length > 0
      ? invite.permissions.map((permission) => sanitizeHTML(permission)).join(", ")
      : "No collections";
  const providerSuffix =
    invite.isAppProvider === true
      ? '<span class="ml-2 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">Provider</span>'
      : "";

  return [
    `<span class="text-xs text-gray-600">${permissionLabels}</span>${providerSuffix}`,
    `<span class="mt-1 block text-xs text-gray-500">Manager: ${sanitizeHTML(getInviteManagerLabel(invite))}</span>`,
  ].join("");
}

function getSortedInvites() {
  const statusOrder = {
    draft: 0,
    sent: 1,
    claimed: 2,
  };

  return [...(appData.userInvites || [])].sort((a, b) => {
    const statusCompare =
      (statusOrder[a?.status] ?? 99) - (statusOrder[b?.status] ?? 99);
    if (statusCompare !== 0) {
      return statusCompare;
    }
    return (a?.name || a?.email || "").localeCompare(
      b?.name || b?.email || "",
    );
  });
}

/**
 * Renders the Users-tab invite management surface.
 * @returns {string} Invite management HTML.
 */
export function renderUserInvitesSection() {
  const sortedInvites = getSortedInvites();
  const visibleInvites = showClaimedInvites
    ? sortedInvites
    : sortedInvites.filter((invite) => invite?.status !== "claimed");

  const rowsHtml =
    visibleInvites.length > 0
      ? visibleInvites
          .map((invite) => {
            const sentLabel = formatInviteTimestamp(invite.lastSentAt);
            const claimedMeta =
              invite.status === "claimed" && invite.claimedAt
                ? `<div class="mt-1 text-[11px] text-gray-500">Claimed ${sanitizeHTML(formatInviteTimestamp(invite.claimedAt))}</div>`
                : "";

            return `
                <tr class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td class="py-3 px-4 align-top" data-mobile-label="Invite">
                        <div class="min-w-[13rem]">
                            <div class="font-medium text-gray-900 break-words">${sanitizeHTML(invite.name || "Unnamed Invite")}</div>
                            <div class="text-xs text-gray-500 break-all">${sanitizeHTML(invite.email || invite.id || "")}</div>
                        </div>
                    </td>
                    <td class="py-3 px-4 align-top whitespace-nowrap" data-mobile-label="Status">
                        <span class="inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getInviteStatusClasses(invite.status)}">${getInviteStatusLabel(invite.status)}</span>
                        ${claimedMeta}
                    </td>
                    <td class="py-3 px-4 align-top whitespace-nowrap" data-mobile-label="Role">
                        <span class="text-sm text-gray-700">${invite.role === "admin" ? "Admin" : "User"}</span>
                    </td>
                    <td class="py-3 px-4 align-top min-w-[14rem]" data-mobile-label="Access">
                        ${renderInviteAccessSummary(invite)}
                    </td>
                    <td class="py-3 px-4 align-top whitespace-nowrap text-sm text-gray-600" data-mobile-label="Last Sent">
                        ${sanitizeHTML(sentLabel)}
                    </td>
                    <td class="py-3 px-4 align-top whitespace-nowrap text-sm text-gray-600" data-mobile-label="Sent By">
                        ${sanitizeHTML(getInviteSenderLabel(invite))}
                    </td>
                    <td class="settings-invite-actions-cell py-3 px-4 align-top whitespace-nowrap" data-mobile-label="Actions">
                        <div class="settings-invite-actions flex items-start justify-end gap-1">
                            ${
                              invite.status !== "claimed"
                                ? `
                                <button type="button" class="btn-edit-invite standard-action-icon-btn" data-invite-id="${sanitizeHTML(invite.id)}" title="Edit invite" aria-label="Edit invite">
                                    <span class="material-symbols-outlined pointer-events-none text-[18px]">edit</span>
                                </button>
                                <button type="button" class="btn-send-invite standard-action-icon-btn" data-invite-id="${sanitizeHTML(invite.id)}" title="Send invite email" aria-label="Send invite email">
                                    <span class="material-symbols-outlined pointer-events-none text-[18px]">mail</span>
                                </button>
                                <button type="button" class="btn-delete-invite standard-action-icon-btn standard-action-icon-btn--danger" data-invite-id="${sanitizeHTML(invite.id)}" title="Delete invite" aria-label="Delete invite">
                                    <span class="material-symbols-outlined pointer-events-none text-[18px]">delete</span>
                                </button>
                            `
                                : `
                                <span class="px-2 py-1 text-xs text-gray-400">No actions</span>
                            `
                            }
                        </div>
                    </td>
                </tr>`;
          })
          .join("")
      : `
            <tr class="settings-invite-empty-row">
                <td colspan="7" class="settings-invite-empty-cell py-6 px-4 text-sm text-center text-gray-500">
                    ${showClaimedInvites ? "No invites found." : "No active invites. Create one to pre-provision access before first login."}
                </td>
            </tr>
        `;

  return `
        <div class="mt-8 bg-white rounded-lg shadow overflow-hidden">
            <div class="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h4 class="text-lg font-semibold text-gray-900">Invites</h4>
                    <p class="mt-1 max-w-3xl text-sm text-gray-600">Invite new users and set up their permissions</p>
                </div>
                <button id="add-user-invite-btn" class="inline-flex items-center rounded-md bg-[var(--dfc-brand-primary)] px-4 py-2 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] shadow-sm transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none whitespace-nowrap shrink-0">
                    New Invite
                </button>
            </div>
            <div class="overflow-x-auto">
                <table class="settings-invites-table min-w-full w-max text-sm text-left table-auto">
                    <thead class="text-xs text-gray-700 uppercase bg-gray-100 border-b">
                        <tr>
                            <th class="py-3 px-4">Invite</th>
                            <th class="py-3 px-4 whitespace-nowrap">Status</th>
                            <th class="py-3 px-4 whitespace-nowrap">Role</th>
                            <th class="py-3 px-4">Access</th>
                            <th class="py-3 px-4 whitespace-nowrap">Last Sent</th>
                            <th class="py-3 px-4 whitespace-nowrap">Sent By</th>
                            <th class="py-3 px-4 text-right whitespace-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        ${rowsHtml}
                    </tbody>
                </table>
            </div>
            <div class="flex justify-end px-4 py-3">
                <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                    <input type="checkbox" id="show-claimed-invites-toggle" class="${NATIVE_CHECKBOX_CLASSES}" ${showClaimedInvites ? "checked" : ""}>
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                    <span>Show claimed invites</span>
                </label>
            </div>
        </div>
    `;
}

/**
 * Handles invite filter changes inside the Users settings tab.
 * @param {HTMLElement} target Event target.
 * @param {{ rerenderUsersTab?: Function }} callbacks Coordinator callbacks.
 * @returns {boolean} True when handled.
 */
export function handleUserInviteSettingsChange(target, callbacks = {}) {
  if (!target.matches("#show-claimed-invites-toggle")) {
    return false;
  }

  showClaimedInvites = target.checked;
  const rerenderUsersTab = callbacks.rerenderUsersTab || (() => {});
  rerenderUsersTab();
  return true;
}

/**
 * Resets local invite UI state.
 */
export function resetUserInviteSettingsState() {
  showClaimedInvites = false;
}

/**
 * Handles invite action buttons inside the Users settings tab.
 * @param {HTMLElement} target Event target.
 * @param {{ rerenderUsersTab?: Function }} callbacks Coordinator callbacks.
 * @returns {Promise<boolean>} True when handled.
 */
export async function handleUserInviteSettingsButtonClick(target, callbacks = {}) {
  const rerenderUsersTab = callbacks.rerenderUsersTab || (() => {});
  if (target.closest("#add-user-invite-btn")) {
    openUserInviteForm();
    return true;
  }

  const editInviteButton = target.closest(".btn-edit-invite");
  if (editInviteButton) {
    const inviteId = editInviteButton.dataset.inviteId;
    const invite = (appData.userInvites || []).find((entry) => entry.id === inviteId);
    if (!invite) {
      openConfirmationModal("Error", "Invite not found.", async () => {}, false, "OK");
      return true;
    }
    openUserInviteForm(invite);
    return true;
  }

  const sendInviteButton = target.closest(".btn-send-invite");
  if (sendInviteButton) {
    const inviteId = sendInviteButton.dataset.inviteId;
    const originalHtml = sendInviteButton.innerHTML;
    sendInviteButton.disabled = true;
    sendInviteButton.innerHTML =
      '<span class="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>';

    try {
      await sendUserInvite(inviteId);
    } catch (error) {
      console.error("Failed to send invite:", error);
      const conflictModal = getInviteConflictModalConfig(error.message);
      openConfirmationModal(
        conflictModal?.title || "Invite Error",
        conflictModal?.body || error.message || "Could not send the invite.",
        async () => {},
        false,
        "OK",
      );
      sendInviteButton.disabled = false;
      sendInviteButton.innerHTML = originalHtml;
    }
    return true;
  }

  const deleteInviteButton = target.closest(".btn-delete-invite");
  if (deleteInviteButton) {
    const inviteId = deleteInviteButton.dataset.inviteId;
    openConfirmationModal(
      "Delete Invite",
      "Delete this unclaimed invite? A later first-time login for this email will fall back to the normal self-provisioning path.",
      async () => {
        try {
          await deleteUserInvite(inviteId);
          rerenderUsersTab();
        } catch (error) {
          console.error("Failed to delete invite:", error);
          openConfirmationModal(
            "Invite Error",
            error.message || "Could not delete the invite.",
            async () => {},
            false,
            "OK",
          );
        }
      },
      true,
      "Delete Invite",
    );
    return true;
  }


  return false;
}
